<template>
  <layout width="428">
    <template slot="header">
      {{ isNew ? 'Add new' : 'Edit' }} parameter
    </template>
    <validation-observer ref="form">
      <v-form ref="form" class="mx-4">
        <subheader>Mandatory fields</subheader>
        <validation-provider
          v-slot="{ errors }"
          name="Parameter name"
          rules="required"
        >
          <v-text-field
            data-test-parameter="name"
            class="mt-2 mb-2"
            label="Parameter name *"
            v-model="argument"
            clearable
            :error-messages="errors"
          ></v-text-field>
        </validation-provider>
        <validation-provider
          v-slot="{ errors }"
          name="Long name"
          rules="required"
        >
          <v-text-field
            data-test-parameter="long name"
            v-model="description"
            class="mt-2 mb-2"
            label="Long name *"
            clearable
            :error-messages="errors"
          ></v-text-field>
        </validation-provider>
        <validation-provider v-slot="{ errors }" name="Type" rules="required">
          <v-select
            :menu-props="{ bottom: true, offsetY: true }"
            data-test-parameter="type"
            :items="typeOptions"
            v-model="typeSpec"
            label="Type *"
            placeholder="Value"
            :error-messages="errors"
            item-text="name"
            item-value="id"
            required
            clearable
          >
            <template v-slot:item="{ item }">
              <div :data-test-select="item">
                {{ item }}
              </div>
            </template>
          </v-select>
        </validation-provider>
        <subheader>Optional fields</subheader>

        <v-text-field
          data-test-parameter="units"
          v-model="units"
          label="Units"
          placeholder="Value"
          clearable
        />

        <v-text-field
          data-test-parameter="regex"
          label="Regex"
          placeholder="Value"
          clearable
          v-model="regex"
        />

        <v-text-field
          data-test-parameter="mask"
          label="Mask"
          placeholder="value"
          clearable
          v-model="mask"
        />

        <v-text-field
          data-test-parameter="default value"
          label="Default value"
          placeholder="value"
          clearable
          v-model="defaultValue"
        />
      </v-form>
    </validation-observer>
    <v-divider />
    <template #footer>
      <v-spacer />
      <v-btn text @click="close">
        cancel
      </v-btn>
      <v-btn
        color="primary"
        data-test-parameter="actionSaveOrAdd"
        text
        @click="handleSave"
      >
        <template v-if="isNew">add</template>
        <template v-else>save</template>
      </v-btn>
    </template>
  </layout>
</template>

<script>
import Layout from '@/layouts/LayoutPopup';
import {
  computed,
  ref,
  onMounted,
  reactive,
  toRefs
} from '@vue/composition-api';

setInteractionMode('eager');

import { required } from 'vee-validate/dist/rules';
import {
  extend,
  ValidationObserver,
  ValidationProvider,
  setInteractionMode
} from 'vee-validate';

extend('required', {
  ...required,
  message: '{_field_} can not be empty'
});

import { useLazyQuery, useMutation, useResult } from '@vue/apollo-composable';

import gql from '@/api/schemas/item.graphql';
import dataTypesSchemas from '@/services/datatypes/schema.graphql';
import useSchemaControlActions from '@/api/schemas/useSchemaControlActions';

export default {
  name: 'EditSchemaControlArgument',
  props: {
    entity: {
      type: [Object],
      default: () => ({})
    },
    entityType: {
      type: String,
      default: ''
    },
    rpcName: {
      type: String,
      default: ''
    },
    linkAfterCreation: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    callbackAfter: {
      type: Function,
      default: () => {}
    }
  },
  components: {
    Layout,
    ValidationObserver,
    ValidationProvider
  },

  setup(props, { emit, root, refs }) {
    const { mutate: editSchemaControl } = useMutation(gql.editSchemaControl);
    const { mutate: createSchemaControl } = useMutation(
      gql.createSchemaControl
    );

    const close = () => {
      emit('close');
    };

    // const dataTypes = useLazyQuery(dataTypesSchemas.getAllTypes, null);

    const localEntity = reactive({
      argument: props.entity?.argument || null,
      defaultValue: props.entity?.defaultValue || null,
      description: props.entity?.description || null,
      mask: props.entity?.mask || null,
      regex: props.entity?.regex || null,
      rpc: props.entity?.rpc || null,
      typeSpec: props.entity?.typeSpec || null,
      units: props.entity?.units || null
    });

    // const typeOptions = useResult(dataTypes.result, [], data => {
    //   return data.propertyTypes;
    // });

    const typeOptions = ['double', 'int', 'number', 'string', 'bool', 'json'];

    const isNew = computed(() => {
      return !props.entity?.id;
    });

    const handleSave = () => {
      refs.form.validate().then(success => {
        if (!success) {
          return;
        }

        if (isNew.value) {
          createSchemaControl({
            input: {
              schemaControl: {
                schemaId: root.$route.params.id,
                argument: localEntity.argument,
                defaultValue: localEntity.defaultValue,
                description: localEntity.description,
                mask: localEntity.mask,
                regex: localEntity.regex,
                rpc: props.rpcName,
                units: localEntity.units,
                typeSpec: localEntity.typeSpec
              }
            }
          }).then(close);
        } else {
          editSchemaControl({
            input: {
              id: props.entity.id,
              patch: {
                argument: localEntity.argument,
                defaultValue: localEntity.defaultValue,
                description: localEntity.description,
                hidden: localEntity.hidden,
                mask: localEntity.mask,
                regex: localEntity.regex,
                rpc: localEntity.rpc,
                units: localEntity.units,
                typeSpec: localEntity.typeSpec
              }
            }
          }).then(close);
        }
      });
    };

    onMounted(() => {
      // dataTypes.load();
    });

    return {
      ...toRefs(localEntity),
      typeOptions,
      handleSave,
      isNew,
      localEntity,
      close
    };
  }
};
</script>
